import type { Coordinates } from './distance'
import type { PublicEvent } from './publicEvent'

export type MapPin = { event: PublicEvent; position: Coordinates }

// Drawn as a Symbol path, so the pin takes any colour.
export const PIN_PATH =
  'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z M -2,-30 a 2,2 0 1,1 4,0 2,2 0 1,1 -4,0'

export const PIN_PATH_HEIGHT = 40

// About 15 m: far enough apart to pick at street level, close enough to read as one venue.
const SPREAD_DEGREES = 0.000135

export function mapPins(events: PublicEvent[]): MapPin[] {
  return spreadPins(
    events.flatMap((event) => {
      const { lat, lng } = event.venue
      return !event.online && lat !== null && lng !== null ? [{ event, position: { lat, lng } }] : []
    })
  )
}

export function spreadPins<Pin extends { position: Coordinates }>(pins: Pin[]): Pin[] {
  const placeOf = ({ position }: Pin) => `${position.lat},${position.lng}`
  const atPlace = new Map<string, Pin[]>()
  pins.forEach((pin) => atPlace.set(placeOf(pin), [...(atPlace.get(placeOf(pin)) ?? []), pin]))

  return pins.map((pin) => {
    const sharing = atPlace.get(placeOf(pin)) ?? [pin]
    if (sharing.length === 1) return pin

    const angle = (2 * Math.PI * sharing.indexOf(pin)) / sharing.length
    const { lat, lng } = pin.position
    return {
      ...pin,
      position: {
        lat: lat + SPREAD_DEGREES * Math.sin(angle),
        lng: lng + (SPREAD_DEGREES * Math.cos(angle)) / Math.cos((lat * Math.PI) / 180),
      },
    }
  })
}
